import apiService from './api.service';
import {
  SubscriptionResponse,
  BillingSummary,
  TiersResponse,
  CheckoutSessionCreate,
  CheckoutSessionResponse,
  PortalSessionResponse,
  SubscriptionActionResponse,
  InvoiceHistoryResponse,
  ProductLine,
  ProductTiersResponse,
  PricingResponse,
} from '../types/billing.types';

class BillingService {
  async getSubscription(): Promise<SubscriptionResponse> {
    const response = await apiService.client.get<SubscriptionResponse>('/billing/subscription');
    return response.data;
  }

  async getBillingSummary(): Promise<BillingSummary> {
    const response = await apiService.client.get<BillingSummary>('/billing/subscription/summary');
    return response.data;
  }

  async getAvailableTiers(): Promise<TiersResponse> {
    const response = await apiService.client.get<TiersResponse>('/billing/tiers');
    return response.data;
  }

  async getProductTiers(productLine: ProductLine): Promise<ProductTiersResponse> {
    const response = await apiService.client.get<ProductTiersResponse>('/billing/tiers', {
      params: { product_line: productLine }
    });
    return response.data;
  }

  async createCheckoutSession(data: CheckoutSessionCreate): Promise<CheckoutSessionResponse> {
    const response = await apiService.client.post<CheckoutSessionResponse>('/billing/checkout', data);
    return response.data;
  }

  async createPortalSession(returnUrl: string): Promise<PortalSessionResponse> {
    const response = await apiService.client.post<PortalSessionResponse>(
      '/billing/portal',
      null,
      {
        params: { return_url: returnUrl }
      }
    );
    return response.data;
  }

  async cancelSubscription(atPeriodEnd: boolean = true): Promise<SubscriptionActionResponse> {
    const response = await apiService.client.post<SubscriptionActionResponse>(
      '/billing/subscription/cancel',
      null,
      {
        params: { at_period_end: atPeriodEnd }
      }
    );
    return response.data;
  }

  async reactivateSubscription(): Promise<SubscriptionActionResponse> {
    const response = await apiService.client.post<SubscriptionActionResponse>(
      '/billing/subscription/reactivate'
    );
    return response.data;
  }

  async upgradeSubscription(tier: string): Promise<SubscriptionResponse> {
    const response = await apiService.client.post<SubscriptionResponse>(
      '/billing/subscription/upgrade',
      { tier }
    );
    return response.data;
  }

  async downgradeSubscription(tier: string): Promise<any> {
    const response = await apiService.client.post<any>(
      '/billing/subscription/downgrade',
      { tier }
    );
    return response.data;
  }

  async cancelPendingDowngrade(): Promise<SubscriptionActionResponse> {
    const response = await apiService.client.post<SubscriptionActionResponse>(
      '/billing/subscription/cancel-pending-downgrade'
    );
    return response.data;
  }

  async getInvoiceHistory(limit: number = 12): Promise<InvoiceHistoryResponse> {
    const response = await apiService.client.get<InvoiceHistoryResponse>(
      '/billing/invoices',
      {
        params: { limit }
      }
    );
    return response.data;
  }

  /**
   * Get current pricing from Stripe.
   * This is the source of truth for all pricing displayed in the app.
   * Prices are cached on the backend after first fetch.
   */
  async getPricing(): Promise<PricingResponse> {
    const response = await apiService.client.get<PricingResponse>('/billing/pricing');
    return response.data;
  }
}

export default new BillingService();
